import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import './magazine.css'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { FiAlignLeft, FiAlignCenter, FiAlignRight, FiLink, FiMinus, FiPlus, FiX } from 'react-icons/fi'

export type RichEditorHandle = {
  getHTML(): string
  getText(): string
  getJSON(): any
  setHTML(html: string): void
  setJSON(json: any): void
  replaceSelectionWithHTML(html: string): void
  appendHTML(html: string): void
  getSelectionText(): string
  isSelectionEmpty(): boolean
  toggleBold(): void
  toggleItalic(): void
  setHeading(level: 1 | 2 | 3 | 4): void
  toggleBulletList(): void
  toggleOrderedList(): void
  toggleBlockquote(): void
  toggleHighlight(): void
  setLink(url: string): void
  unsetLink(): void
  insertImage(url: string, alt?: string): void
  setImageSize(size: 'xsmall' | 'small' | 'medium' | 'large'): void
  setImageAlign(align: 'left' | 'right' | 'center'): void
  setImageWrap(wrap: 'tight' | 'normal' | 'airy'): void
  hasActiveImage(): boolean
  getActiveImageLink(): string
  setActiveImageLink(url: string): void
  unsetActiveImageLink(): void
  toggleStrike(): void
  toggleCode(): void
  toggleCodeBlock(): void
  insertDivider(): void
  undo(): void
  redo(): void
  clearFormatting(): void
  toggleUnderline(): void
  setTextAlign(alignment: 'left' | 'center' | 'right' | 'justify'): void
  refreshToolbarState(): void
}

export type EditorToolbarState = {
  // marks
  bold: boolean
  italic: boolean
  underline: boolean
  strike: boolean
  code: boolean
  highlight: boolean
  link: boolean
  // blocks
  h1: boolean
  h2: boolean
  h3: boolean
  h4: boolean
  bullet: boolean
  ordered: boolean
  quote: boolean
  codeBlock: boolean
  // alignment
  align: 'left' | 'center' | 'right' | 'justify'
  // history
  canUndo: boolean
  canRedo: boolean
}

type Props = {
  initialHTML?: string
  onChange?: (html: string, text: string) => void
  onJsonChange?: (json: any) => void
  className?: string
  onToolbarState?: (state: EditorToolbarState) => void
  onRequestImageLink?: (currentUrl: string) => void
}

function toHTMLParagraphs(text: string): string {
  const parts = String(text || '').split(/\r?\n+/).filter(Boolean)
  if (!parts.length) return '<p></p>'
  return parts.map(p => `<p>${escapeHtml(p)}</p>`).join('')
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function normalizeImageClass(current?: string | null): string[] {
  const tokens = String(current || '')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
  const filtered = tokens.filter((t) => !/^mag-image(--(xsmall|small|medium|large|left|right|center|tight|normal|airy))?$/.test(t))
  return ['mag-image', ...filtered]
}

function withImageSize(currentClass: string | undefined, size: 'xsmall' | 'small' | 'medium' | 'large'): string {
  const raw = String(currentClass || '')
  const align: 'left' | 'right' | 'center' =
    raw.includes('mag-image--left') ? 'left' : raw.includes('mag-image--center') ? 'center' : 'right'
  const wrap: 'tight' | 'normal' | 'airy' =
    raw.includes('mag-image--tight') ? 'tight' : raw.includes('mag-image--airy') ? 'airy' : 'normal'
  const base = normalizeImageClass(currentClass)
  return [...base, `mag-image--${size}`, `mag-image--${align}`, `mag-image--${wrap}`].join(' ')
}

function withImageAlign(currentClass: string | undefined, align: 'left' | 'right' | 'center'): string {
  const raw = String(currentClass || '')
  const size: 'xsmall' | 'small' | 'medium' | 'large' =
    raw.includes('mag-image--large') ? 'large' : raw.includes('mag-image--medium') ? 'medium' : raw.includes('mag-image--xsmall') ? 'xsmall' : 'small'
  const wrap: 'tight' | 'normal' | 'airy' =
    raw.includes('mag-image--tight') ? 'tight' : raw.includes('mag-image--airy') ? 'airy' : 'normal'
  const base = normalizeImageClass(currentClass)
  return [...base, `mag-image--${size}`, `mag-image--${align}`, `mag-image--${wrap}`].join(' ')
}

function withImageWrap(currentClass: string | undefined, wrap: 'tight' | 'normal' | 'airy'): string {
  const raw = String(currentClass || '')
  const size: 'xsmall' | 'small' | 'medium' | 'large' =
    raw.includes('mag-image--large') ? 'large' : raw.includes('mag-image--medium') ? 'medium' : raw.includes('mag-image--xsmall') ? 'xsmall' : 'small'
  const align: 'left' | 'right' | 'center' =
    raw.includes('mag-image--left') ? 'left' : raw.includes('mag-image--center') ? 'center' : 'right'
  const base = normalizeImageClass(currentClass)
  return [...base, `mag-image--${size}`, `mag-image--${align}`, `mag-image--${wrap}`].join(' ')
}

function normalizeImageLink(input: string): string {
  const value = String(input || '').trim()
  if (!value) return ''
  if (/^(https?:\/\/|mailto:|tel:|\/)/i.test(value)) return value
  return `https://${value}`
}

export const RichEditor = React.forwardRef<RichEditorHandle, Props>(function RichEditor(
  { initialHTML = '', onChange, onJsonChange, className, onToolbarState, onRequestImageLink },
  ref
) {
  const initialContentRef = useRef<string>(initialHTML || '<p></p>')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imageToolsRef = useRef<HTMLDivElement | null>(null)
  const draggedImagePosRef = useRef<number | null>(null)
  const activeImagePosRef = useRef<number | null>(null)
  const [imageTools, setImageTools] = useState<{
    visible: boolean
    top: number
    left: number
    size: 'xsmall' | 'small' | 'medium' | 'large'
    align: 'left' | 'right' | 'center'
    wrap: 'tight' | 'normal' | 'airy'
    linkHref: string
  }>({
    visible: false,
    top: 0,
    left: 0,
    size: 'small',
    align: 'right',
    wrap: 'normal',
    linkHref: '',
  })

  function updateImageRemoveOverlay(ed?: Editor | null) {
    const activeEditor = ed || editor
    const host = containerRef.current
    if (!activeEditor || !host) {
      setImageTools((p) => (p.visible ? { ...p, visible: false } : p))
      return
    }

    let pos: number | null = null
    let nodeEl: HTMLElement | null = null
    let attrs: Record<string, any> = {}

    if (activeEditor.isActive('image')) {
      const selectedPos = (activeEditor.state.selection as any)?.from
      if (typeof selectedPos === 'number') {
        pos = selectedPos
        nodeEl = activeEditor.view.nodeDOM(selectedPos) as HTMLElement | null
        attrs = (activeEditor.getAttributes('image') || {}) as Record<string, any>
      }
    } else if (activeImagePosRef.current != null) {
      const rememberedPos = activeImagePosRef.current
      const rememberedNode = activeEditor.state.doc.nodeAt(rememberedPos)
      if (rememberedNode && rememberedNode.type?.name === 'image') {
        pos = rememberedPos
        nodeEl = activeEditor.view.nodeDOM(rememberedPos) as HTMLElement | null
        attrs = (rememberedNode.attrs || {}) as Record<string, any>
      }
    }

    if (pos == null || !nodeEl) {
      activeImagePosRef.current = null
      setImageTools((p) => (p.visible ? { ...p, visible: false } : p))
      return
    }

    activeImagePosRef.current = pos
    const cls = String(attrs.class || '')
    const size: 'xsmall' | 'small' | 'medium' | 'large' =
      cls.includes('mag-image--large') ? 'large' : cls.includes('mag-image--medium') ? 'medium' : cls.includes('mag-image--xsmall') ? 'xsmall' : 'small'
    const align: 'left' | 'right' | 'center' =
      cls.includes('mag-image--left') ? 'left' : cls.includes('mag-image--center') ? 'center' : 'right'
    const wrap: 'tight' | 'normal' | 'airy' =
      cls.includes('mag-image--tight') ? 'tight' : cls.includes('mag-image--airy') ? 'airy' : 'normal'
    const linkHref = String(attrs.link_href || '')
    const hostRect = host.getBoundingClientRect()
    const rect = nodeEl.getBoundingClientRect()
    const left = Math.max(8, Math.min(hostRect.width - 356, rect.right - hostRect.left - 348))
    const top = Math.max(8, rect.top - hostRect.top + 6)
    setImageTools({ visible: true, top, left, size, align, wrap, linkHref })
  }

  function patchActiveImageAttrs(attrsPatch: Record<string, any>) {
    if (!editor) return
    if (editor.isActive('image')) {
      const attrs = (editor.getAttributes('image') || {}) as Record<string, any>
      ;(editor as any).chain().focus().updateAttributes('image', { ...attrs, ...attrsPatch }).run?.()
      updateImageRemoveOverlay(editor)
      return
    }
    const pos = activeImagePosRef.current
    if (pos == null) return
    const node = editor.state.doc.nodeAt(pos)
    if (!node || node.type.name !== 'image') return
    const nextAttrs = { ...(node.attrs || {}), ...attrsPatch }
    const tr = editor.state.tr.setNodeMarkup(pos, undefined, nextAttrs)
    editor.view.dispatch(tr)
    updateImageRemoveOverlay(editor)
  }

  function setActiveImageSize(nextSize: 'xsmall' | 'small' | 'medium' | 'large') {
    const currentClass = editor?.isActive('image')
      ? String((editor.getAttributes('image') || {}).class || '')
      : String((editor && activeImagePosRef.current != null ? editor.state.doc.nodeAt(activeImagePosRef.current)?.attrs?.class : '') || '')
    const nextClass = withImageSize(currentClass, nextSize)
    patchActiveImageAttrs({ class: nextClass })
  }

  function setActiveImageAlign(nextAlign: 'left' | 'right' | 'center') {
    const currentClass = editor?.isActive('image')
      ? String((editor.getAttributes('image') || {}).class || '')
      : String((editor && activeImagePosRef.current != null ? editor.state.doc.nodeAt(activeImagePosRef.current)?.attrs?.class : '') || '')
    const nextClass = withImageAlign(currentClass, nextAlign)
    patchActiveImageAttrs({ class: nextClass })
  }

  function setActiveImageWrap(nextWrap: 'tight' | 'normal' | 'airy') {
    const currentClass = editor?.isActive('image')
      ? String((editor.getAttributes('image') || {}).class || '')
      : String((editor && activeImagePosRef.current != null ? editor.state.doc.nodeAt(activeImagePosRef.current)?.attrs?.class : '') || '')
    const nextClass = withImageWrap(currentClass, nextWrap)
    patchActiveImageAttrs({ class: nextClass })
  }

  function applyActiveImageLink(nextLink: string | null) {
    const normalized = normalizeImageLink(nextLink || '')
    patchActiveImageAttrs({ link_href: normalized || null })
    setImageTools((p) => ({ ...p, linkHref: normalized }))
  }

  function hasActiveImageNode(activeEditor?: Editor | null): boolean {
    const ed = activeEditor || editor
    if (!ed) return false
    if (ed.isActive('image')) return true
    const pos = activeImagePosRef.current
    if (pos == null) return false
    const node = ed.state.doc.nodeAt(pos)
    return Boolean(node && node.type?.name === 'image')
  }

  function getActiveImageLinkValue(activeEditor?: Editor | null): string {
    const ed = activeEditor || editor
    if (!ed) return ''
    if (ed.isActive('image')) return String((ed.getAttributes('image') || {}).link_href || '')
    const pos = activeImagePosRef.current
    if (pos == null) return ''
    const node = ed.state.doc.nodeAt(pos)
    if (!node || node.type?.name !== 'image') return ''
    return String((node.attrs as any)?.link_href || '')
  }
  const MagazineImage = useMemo(
    () =>
      Image.extend({
        draggable: true,
        addAttributes() {
          return {
            ...(this.parent?.() || {}),
            class: {
              default: 'mag-image mag-image--small mag-image--right mag-image--normal',
              parseHTML: (element: HTMLElement) => element.getAttribute('class'),
              renderHTML: (attributes: Record<string, any>) => {
                if (!attributes.class) return {}
                return { class: attributes.class }
              },
            },
            link_href: {
              default: null,
              parseHTML: (element: HTMLElement) => element.getAttribute('data-link-href'),
              renderHTML: (attributes: Record<string, any>) => {
                if (!attributes.link_href) return {}
                return { 'data-link-href': attributes.link_href }
              },
            },
          }
        },
      }),
    []
  )
  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4] },
      bulletList: { keepMarks: true },
      orderedList: { keepMarks: true },
    }),
    Highlight,
    Underline,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Link.configure({
      autolink: true,
      openOnClick: false,
      HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
    }),
    MagazineImage.configure({
      HTMLAttributes: {},
    }),
  ], [MagazineImage])

  const editor = useEditor({
    extensions,
    // Keep editor uncontrolled during typing: only seed content once on mount.
    // Parent prop updates should not reset/caret-jump the live document.
    content: initialContentRef.current,
    editorProps: {
      attributes: {
        class: 'min-h-64 w-full rounded-xl bg-white/5 border border-white/10 px-5 py-5 outline-none tiptap-mag',
      },
      handleKeyDown(view, event) {
        if ((event.key === 'Backspace' || event.key === 'Delete') && (editor as any)?.isActive?.('image')) {
          ;(editor as any)?.chain().focus().deleteSelection().run?.()
          return true
        }
        return false
      },
      handleDOMEvents: {
        dragstart(view, event) {
          const e = event as DragEvent
          const target = e.target as HTMLElement | null
          if (!target || target.tagName !== 'IMG' || !target.classList.contains('mag-image')) return false
          try {
            const pos = view.posAtDOM(target, 0)
            draggedImagePosRef.current = pos
            e.dataTransfer?.setData('text/plain', 'mag-image')
            e.dataTransfer!.effectAllowed = 'move'
          } catch {
            draggedImagePosRef.current = null
          }
          return false
        },
        dragend() {
          draggedImagePosRef.current = null
          return false
        },
        drop(view, event) {
          const e = event as DragEvent
          const fromPos = draggedImagePosRef.current
          if (fromPos == null) return false
          const node = view.state.doc.nodeAt(fromPos)
          const drop = view.posAtCoords({ left: e.clientX, top: e.clientY })
          draggedImagePosRef.current = null
          if (!node || node.type.name !== 'image' || !drop) return false
          let insertPos = drop.pos
          if (insertPos > fromPos) insertPos = Math.max(0, insertPos - node.nodeSize)
          if (insertPos === fromPos) return false
          const tr = view.state.tr.delete(fromPos, fromPos + node.nodeSize).insert(insertPos, node)
          view.dispatch(tr.scrollIntoView())
          e.preventDefault()
          return true
        },
      },
    },
    onUpdate({ editor }) {
      const html = editor.getHTML()
      const text = editor.getText().replace(/\u00A0/g, ' ').trim()
      onChange?.(html, text)
      onJsonChange?.(editor.getJSON())
      onToolbarState?.(computeToolbarState(editor))
      updateImageRemoveOverlay(editor)
    },
    onSelectionUpdate({ editor }) {
      onToolbarState?.(computeToolbarState(editor))
      updateImageRemoveOverlay(editor)
    },
  }) as Editor | null

  // expose imperative API
  useImperativeHandle(ref, (): RichEditorHandle => ({
    getHTML() { return editor?.getHTML() || '' },
    getText() { return (editor?.getText() || '').replace(/\u00A0/g, ' ').trim() },
    getJSON() { return editor?.getJSON() },
    isSelectionEmpty() { try { const sel = (editor as any)?.state?.selection; return !!sel && sel.empty } catch { return true } },
    setHTML(html: string) {
      if (!editor) return
      editor.commands.setContent(html || '<p></p>', false)
    },
    setJSON(json: any) {
      if (!editor) return
      editor.commands.setContent(json || { type: 'doc', content: [{ type: 'paragraph' }] }, false)
    },
    replaceSelectionWithHTML(html: string) {
      if (!editor) return
      const fragment = (html && /<\w+[^>]*>/.test(html)) ? html : toHTMLParagraphs(html)
      editor.commands.insertContent(fragment)
    },
    appendHTML(html: string) {
      if (!editor) return
      const fragment = (html && /<\w+[^>]*>/.test(html)) ? html : toHTMLParagraphs(html)
      editor.commands.focus('end')
      editor.commands.insertContent(fragment)
    },
    getSelectionText() {
      if (!editor) return ''
      const { from, to } = editor.state.selection
      const slice = editor.state.doc.textBetween(from, to, '\n')
      return slice?.trim() || ''
    },
    toggleBold() { editor?.chain().focus().toggleBold().run() },
    toggleItalic() { editor?.chain().focus().toggleItalic().run() },
    setHeading(level: 1 | 2 | 3 | 4) { editor?.chain().focus().setHeading({ level }).run() },
    toggleBulletList() { editor?.chain().focus().toggleBulletList().run() },
    toggleOrderedList() { editor?.chain().focus().toggleOrderedList().run() },
    toggleBlockquote() { editor?.chain().focus().toggleBlockquote().run() },
    toggleHighlight() { (editor as any)?.chain().focus().toggleHighlight?.().run?.() },
    setLink(url: string) { editor?.chain().focus().setLink({ href: url }).run() },
    unsetLink() { editor?.chain().focus().unsetLink().run() },
    insertImage(url: string, alt?: string) {
      (editor as any)?.chain().focus().setImage?.({
        src: url,
        alt,
        class: 'mag-image mag-image--small mag-image--right mag-image--normal',
      }).run?.()
    },
    setImageSize(size: 'xsmall' | 'small' | 'medium' | 'large') {
      if (!editor?.isActive('image')) return
      const attrs = (editor.getAttributes('image') || {}) as Record<string, any>
      const nextClass = withImageSize(String(attrs.class || ''), size)
      ;(editor as any).chain().focus().updateAttributes('image', { ...attrs, class: nextClass }).run?.()
    },
    setImageAlign(align: 'left' | 'right' | 'center') {
      if (!editor?.isActive('image')) return
      const attrs = (editor.getAttributes('image') || {}) as Record<string, any>
      const nextClass = withImageAlign(String(attrs.class || ''), align)
      ;(editor as any).chain().focus().updateAttributes('image', { ...attrs, class: nextClass }).run?.()
    },
    setImageWrap(wrap: 'tight' | 'normal' | 'airy') {
      if (!editor?.isActive('image')) return
      const attrs = (editor.getAttributes('image') || {}) as Record<string, any>
      const nextClass = withImageWrap(String(attrs.class || ''), wrap)
      ;(editor as any).chain().focus().updateAttributes('image', { ...attrs, class: nextClass }).run?.()
    },
    hasActiveImage() {
      return hasActiveImageNode(editor)
    },
    getActiveImageLink() {
      return getActiveImageLinkValue(editor)
    },
    setActiveImageLink(url: string) {
      applyActiveImageLink(url || '')
    },
    unsetActiveImageLink() {
      applyActiveImageLink(null)
    },
    toggleStrike() { editor?.chain().focus().toggleStrike().run() },
    toggleCode() { editor?.chain().focus().toggleCode().run() },
    toggleCodeBlock() { editor?.chain().focus().toggleCodeBlock().run() },
    insertDivider() { editor?.chain().focus().setHorizontalRule().run() },
    undo() { editor?.chain().focus().undo().run() },
    redo() { editor?.chain().focus().redo().run() },
    clearFormatting() { editor?.chain().focus().unsetAllMarks().clearNodes().run() },
    toggleUnderline() { (editor as any)?.chain().focus().toggleUnderline?.().run?.() },
    setTextAlign(alignment: 'left' | 'center' | 'right' | 'justify') { (editor as any)?.chain().focus().setTextAlign?.(alignment).run?.() },
    refreshToolbarState() { if (editor && onToolbarState) onToolbarState(computeToolbarState(editor)) },
  }), [editor])

  // apply initial content once when editor mounts
  useEffect(() => {
    if (!editor) return
    if (initialHTML) editor.commands.setContent(initialHTML, false)
    // emit initial toolbar state
    if (editor && onToolbarState) onToolbarState(computeToolbarState(editor))
    updateImageRemoveOverlay(editor)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor])

  useEffect(() => {
    if (!editor) return
    const onWindowChange = () => updateImageRemoveOverlay(editor)
    window.addEventListener('resize', onWindowChange)
    window.addEventListener('scroll', onWindowChange, true)
    return () => {
      window.removeEventListener('resize', onWindowChange)
      window.removeEventListener('scroll', onWindowChange, true)
    }
  }, [editor])

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!imageTools.visible) return
      const target = event.target as HTMLElement | null
      if (!target) return
      if (imageToolsRef.current?.contains(target)) return
      if (target.tagName === 'IMG' && target.classList.contains('mag-image')) return
      activeImagePosRef.current = null
      setImageTools((p) => ({ ...p, visible: false }))
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [imageTools.visible])

  if (!editor) return null
  return (
    <div className={['relative', className].filter(Boolean).join(' ')} ref={containerRef}>
      {imageTools.visible ? (
        <div
          ref={imageToolsRef}
          className="absolute z-20 flex items-center gap-1 rounded-lg border border-white/20 bg-black/80 p-1 text-white shadow-lg backdrop-blur"
          style={{ top: imageTools.top, left: imageTools.left }}
        >
          <button
            type="button"
            title="Move left"
            aria-label="Move image left"
            className={`h-6 w-6 rounded text-xs inline-flex items-center justify-center ${imageTools.align === 'left' ? 'bg-white/20' : 'hover:bg-white/15'}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setActiveImageAlign('left')}
          >
            <FiAlignLeft />
          </button>
          <button
            type="button"
            title="Move center"
            aria-label="Move image center"
            className={`h-6 w-6 rounded text-xs inline-flex items-center justify-center ${imageTools.align === 'center' ? 'bg-white/20' : 'hover:bg-white/15'}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setActiveImageAlign('center')}
          >
            <FiAlignCenter />
          </button>
          <button
            type="button"
            title="Move right"
            aria-label="Move image right"
            className={`h-6 w-6 rounded text-xs inline-flex items-center justify-center ${imageTools.align === 'right' ? 'bg-white/20' : 'hover:bg-white/15'}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setActiveImageAlign('right')}
          >
            <FiAlignRight />
          </button>
          <span className="mx-0.5 h-4 w-px bg-white/20" />
          <button
            type="button"
            title="Tight wrap"
            aria-label="Tight image wrap"
            className={`h-6 rounded px-1.5 text-[10px] ${imageTools.wrap === 'tight' ? 'bg-white/20' : 'hover:bg-white/15'}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setActiveImageWrap('tight')}
          >
            T
          </button>
          <button
            type="button"
            title="Normal wrap"
            aria-label="Normal image wrap"
            className={`h-6 rounded px-1.5 text-[10px] ${imageTools.wrap === 'normal' ? 'bg-white/20' : 'hover:bg-white/15'}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setActiveImageWrap('normal')}
          >
            N
          </button>
          <button
            type="button"
            title="Airy wrap"
            aria-label="Airy image wrap"
            className={`h-6 rounded px-1.5 text-[10px] ${imageTools.wrap === 'airy' ? 'bg-white/20' : 'hover:bg-white/15'}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setActiveImageWrap('airy')}
          >
            A
          </button>
          <span className="mx-0.5 h-4 w-px bg-white/20" />
          <button
            type="button"
            title="Smaller"
            aria-label="Smaller image"
            className="h-6 w-6 rounded text-xs inline-flex items-center justify-center hover:bg-white/15"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (imageTools.size === 'large') setActiveImageSize('medium')
              else if (imageTools.size === 'medium') setActiveImageSize('small')
              else setActiveImageSize('xsmall')
            }}
          >
            <FiMinus />
          </button>
          <button
            type="button"
            title="Larger"
            aria-label="Larger image"
            className="h-6 w-6 rounded text-xs inline-flex items-center justify-center hover:bg-white/15"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (imageTools.size === 'xsmall') setActiveImageSize('small')
              else if (imageTools.size === 'small') setActiveImageSize('medium')
              else setActiveImageSize('large')
            }}
          >
            <FiPlus />
          </button>
          <span className="mx-0.5 h-4 w-px bg-white/20" />
          <button
            type="button"
            title="Remove image"
            aria-label="Remove image"
            className="h-6 w-6 rounded text-sm leading-none inline-flex items-center justify-center hover:bg-red-500/35"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              editor.chain().focus().deleteSelection().run()
              activeImagePosRef.current = null
              setImageTools((p) => ({ ...p, visible: false }))
            }}
          >
            <FiX />
          </button>
          <span className="mx-0.5 h-4 w-px bg-white/20" />
          <button
            type="button"
            title={imageTools.linkHref ? 'Edit image link' : 'Add image link'}
            aria-label={imageTools.linkHref ? 'Edit image link' : 'Add image link'}
            className={`h-6 w-6 rounded text-xs inline-flex items-center justify-center ${imageTools.linkHref ? 'bg-brand/35' : 'hover:bg-white/15'}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onRequestImageLink?.(imageTools.linkHref || '')
            }}
          >
            <FiLink />
          </button>
          {imageTools.linkHref ? (
            <button
              type="button"
              title="Remove image link"
              aria-label="Remove image link"
              className="h-6 rounded px-2 text-[10px] hover:bg-white/15"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                applyActiveImageLink(null)
              }}
            >
              Unlink
            </button>
          ) : null}
        </div>
      ) : null}
      <EditorContent editor={editor} />
    </div>
  )
})

export default RichEditor

function computeToolbarState(editor: Editor): EditorToolbarState {
  const is = (name: any, attrs?: any) => (editor?.isActive as any)?.(name, attrs) || false
  const hasStored = (markName: string) => {
    try {
      const sm = (editor as any)?.state?.storedMarks as any[] | null
      const type = (editor as any)?.schema?.marks?.[markName]
      if (!sm || !type) return false
      return sm.some((m: any) => m?.type === type)
    } catch { return false }
  }
  const align = is({ textAlign: 'center' })
    ? 'center'
    : is({ textAlign: 'right' })
    ? 'right'
    : is({ textAlign: 'justify' })
    ? 'justify'
    : 'left'
  return {
    bold: is('bold') || hasStored('bold'),
    italic: is('italic') || hasStored('italic'),
    underline: is('underline') || hasStored('underline'),
    strike: is('strike') || hasStored('strike'),
    code: is('code') || hasStored('code'),
    highlight: is('highlight') || hasStored('highlight'),
    link: is('link'),
    h1: is('heading', { level: 1 }),
    h2: is('heading', { level: 2 }),
    h3: is('heading', { level: 3 }),
    h4: is('heading', { level: 4 }),
    bullet: is('bulletList'),
    ordered: is('orderedList'),
    quote: is('blockquote'),
    codeBlock: is('codeBlock'),
    align,
    canUndo: !!(editor?.can().undo?.()),
    canRedo: !!(editor?.can().redo?.()),
  }
}
